import React, { useCallback, useEffect, useMemo, useState } from 'react'
import get from 'lodash/get'
import rfdc from 'rfdc'

import ComponentList from './ComponentList'
import ComponentSidebar from './ComponentSidebar'
import shortid from 'shortid'

import './ComponentManager.scss'

const clone = rfdc()

const mapComponents = (content) => {
  if (Array.isArray(content)) {
    return content.map((c) => {
      if (Array.isArray(c)) {
        return mapComponents(c)
      }
      return {
        ...c,
        meta: { ...c.meta, id: c.meta?.id ||shortid() },
        content: mapComponents(c.content),
      }
    })
  }
  return content
}

const ComponentManager = ({ components, configs, SidebarHeader, type, features, disabled, onUpdate }) => {
  const [selector, setSelector] = useState('')
  const [localComponents, setComponents] = useState(components || [])

  const selectorChain = useMemo(
    () =>
      selector
        ? selector
            .replace(/^.*?-/g, '-')
            .replaceAll(/-(\d+)/g, ';$1;content')
            .replaceAll(/_(\d+)/g, ';$1')
            .split(';')
            .slice(1, -1)
        : [],
    [selector]
  )

  const component = useMemo(() => {
    if (selectorChain) {
      return selectorChain && get(localComponents, selectorChain)
    }
  }, [selectorChain, localComponents])

  const onComponentsUpdate = (components) => {
    const orderedComponents = components.map((c, idx) => (c.meta ? { ...c, meta: { ...c.meta, order: idx } } : c))
    setComponents(orderedComponents)
    onUpdate(orderedComponents)
  }

  const onComponentUpdate = useCallback(
    (updatedComponent) => {
      setComponents((cmps) => {
        const componentIndex = selectorChain[selectorChain.length - 1]
        const parentChain = selectorChain.slice(0, -1)

        // create copy of components array that can be safely mutated
        let _components = clone(cmps)

        // get nested component's parent, or component array for top level
        let _parent = get(_components, parentChain) || _components

        // update component _parent array and by reference, in _components
        _parent[componentIndex] = updatedComponent

        onUpdate(_components)
        return _components
      })
    },
    [selectorChain]
  )

  const onComponentDelete = useCallback(() => {
    setComponents((cmps) => {
      const parentChain = selectorChain.slice(0, -1)
      const componentIndex = selectorChain[selectorChain.length - 1]

      // create copy of components array that can be safely mutated
      let _components = clone(cmps)

      // get nested component's parent, or component array for top level
      let _parent = get(_components, parentChain) || _components

      // use splice to remove element from _parent array and by reference, from _components
      _parent.splice(componentIndex, 1)
      onUpdate(_components)
      return _components
    })
  }, [selectorChain])

  useEffect(() => {
    setComponents(mapComponents(components))
  }, [components])

  return (
    <div className='ComponentManager' onMouseDown={() => setSelector('')}>
      <div className='ComponentManager__workspace'>
        <ComponentList
          type={type}
          components={localComponents}
          configs={configs}
          selection={selector}
          onSelect={setSelector}
          onComponentsUpdate={onComponentsUpdate}
          disabled={disabled}
        />
      </div>
      <ComponentSidebar
        component={component}
        configs={configs}
        features={features}
        SidebarHeader={SidebarHeader}
        onComponentSave={onComponentUpdate}
        onComponentDelete={onComponentDelete}
        disabled={disabled}
        projectType={type}
      />
    </div>
  )
}

export default ComponentManager
